import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronRight, ArrowRight, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { categoriesAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

export function CatalogPage() {
  const { t, language } = useLanguage();
  const { category, subcategory } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [allCategories, setAllCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [filters, setFilters] = useState({
    material: searchParams.get('material') || '',
    usage: searchParams.get('usage') || '',
    customization: searchParams.get('customization') || '',
  });

  // Organize categories into hierarchy
  const { parentCategories, categoryMap } = useMemo(() => {
    const parents = allCategories.filter(c => !c.parent_id);
    const map = {};
    allCategories.forEach(c => {
      map[c.slug] = c;
      map[c.category_id] = c;
    });
    return { parentCategories: parents, categoryMap: map };
  }, [allCategories]);

  // Get subcategories for a parent
  const getSubcategories = (parentId) => {
    return allCategories.filter(c => c.parent_id === parentId);
  };

  // Current category/subcategory info
  const currentSlug = subcategory || category;
  const currentCategory = currentSlug ? categoryMap[currentSlug] : null;
  const parentCategory = currentCategory?.parent_id ? categoryMap[currentCategory.parent_id] : null;

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [category, subcategory, filters, searchParams, allCategories]);

  // Auto-expand parent category when a subcategory is selected
  useEffect(() => {
    if (currentCategory?.parent_id) {
      setExpandedCategories(prev => ({
        ...prev,
        [currentCategory.parent_id]: true
      }));
    } else if (currentCategory) {
      setExpandedCategories(prev => ({
        ...prev,
        [currentCategory.category_id]: true
      }));
    }
  }, [currentCategory]);

  const loadCategories = async () => {
    try {
      const res = await categoriesAPI.getAll();
      setAllCategories(res.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      let categoryId;
      
      if (currentCategory) {
        // If it's a parent category, get all products from its subcategories
        if (!currentCategory.parent_id) {
          const subcats = getSubcategories(currentCategory.category_id);
          if (subcats.length > 0) {
            // We'll need to modify the API to support multiple category IDs
            // For now, get products from all subcategories
            categoryId = [currentCategory.category_id, ...subcats.map(s => s.category_id)].join(',');
          } else {
            categoryId = currentCategory.category_id;
          }
        } else {
          categoryId = currentCategory.category_id;
        }
      }
      
      const params = {
        category: categoryId || undefined,
        material: filters.material || undefined,
        usage: filters.usage || undefined,
        customization: filters.customization || undefined,
        search: searchParams.get('search') || undefined,
        limit: 100,
      };
      
      const res = await productsAPI.getAll(params);
      setProducts(res.data?.products || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ material: '', usage: '', customization: '' });
    setSearchQuery('');
    setSearchParams(new URLSearchParams());
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const hasActiveFilters = filters.material || filters.usage || filters.customization || searchParams.get('search');

  const materials = ['PVC', 'Metal', 'Paper', 'PET', 'Epoxy', 'Acrylic', 'Silicone', 'Wood', 'Bamboo', 'ABS'];
  const usages = [
    { value: 'indoor', label: language === 'fr' ? 'Intérieur' : 'Indoor' },
    { value: 'outdoor', label: language === 'fr' ? 'Extérieur' : 'Outdoor' },
  ];
  const customizations = [
    { value: 'basic', label: language === 'fr' ? 'Basique' : 'Basic' },
    { value: 'standard', label: 'Standard' },
    { value: 'full', label: language === 'fr' ? 'Complet' : 'Full' },
  ];

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories with hierarchy */}
      <div>
        <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider">
          {language === 'fr' ? 'Catégories' : 'Categories'}
        </h4>
        <div className="space-y-1">
          <Link
            to="/catalogue"
            className={`block py-2 text-sm transition-colors ${!currentSlug ? 'text-[#1F2937] font-medium' : 'text-muted-foreground hover:text-[#1F2937]'}`}
          >
            {language === 'fr' ? 'Tous les produits' : 'All products'}
          </Link>
          
          {parentCategories.map((cat) => {
            const subcats = getSubcategories(cat.category_id);
            const isExpanded = expandedCategories[cat.category_id];
            const isActive = currentCategory?.category_id === cat.category_id || 
                           currentCategory?.parent_id === cat.category_id;
            
            return (
              <div key={cat.category_id} className="border-l-2 border-transparent hover:border-[#DC2626] transition-colors">
                <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(cat.category_id)}>
                  <div className="flex items-center">
                    <Link
                      to={`/catalogue/${cat.slug}`}
                      className={`flex-1 py-2 pl-2 text-sm transition-colors ${
                        currentCategory?.category_id === cat.category_id && !subcategory
                          ? 'text-[#1F2937] font-medium' 
                          : 'text-muted-foreground hover:text-[#1F2937]'
                      }`}
                    >
                      {language === 'fr' ? cat.name_fr : cat.name_en}
                    </Link>
                    {subcats.length > 0 && (
                      <CollapsibleTrigger asChild>
                        <button className="p-2 hover:bg-secondary rounded transition-colors">
                          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </CollapsibleTrigger>
                    )}
                  </div>
                  
                  {subcats.length > 0 && (
                    <CollapsibleContent>
                      <div className="ml-4 border-l border-border pl-2 space-y-1">
                        {subcats.map((subcat) => (
                          <Link
                            key={subcat.category_id}
                            to={`/catalogue/${cat.slug}/${subcat.slug}`}
                            className={`block py-1.5 text-sm transition-colors ${
                              currentCategory?.category_id === subcat.category_id
                                ? 'text-[#DC2626] font-medium' 
                                : 'text-muted-foreground hover:text-[#1F2937]'
                            }`}
                          >
                            {language === 'fr' ? subcat.name_fr : subcat.name_en}
                          </Link>
                        ))}
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </div>
            );
          })}
        </div>
      </div>

      {/* Materials */}
      <div>
        <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider">
          {t('products.materials')}
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {materials.map((material) => (
            <div key={material} className="flex items-center space-x-2">
              <Checkbox
                id={`material-${material}`}
                checked={filters.material === material}
                onCheckedChange={(checked) => handleFilterChange('material', checked ? material : '')}
              />
              <Label htmlFor={`material-${material}`} className="text-sm cursor-pointer">
                {material}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Usage */}
      <div>
        <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider">
          {language === 'fr' ? 'Usage' : 'Usage'}
        </h4>
        <div className="space-y-2">
          {usages.map((usage) => (
            <div key={usage.value} className="flex items-center space-x-2">
              <Checkbox
                id={`usage-${usage.value}`}
                checked={filters.usage === usage.value}
                onCheckedChange={(checked) => handleFilterChange('usage', checked ? usage.value : '')}
              />
              <Label htmlFor={`usage-${usage.value}`} className="text-sm cursor-pointer">
                {usage.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Customization */}
      <div>
        <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider">
          {t('products.customization')}
        </h4>
        <div className="space-y-2">
          {customizations.map((level) => (
            <div key={level.value} className="flex items-center space-x-2">
              <Checkbox
                id={`customization-${level.value}`}
                checked={filters.customization === level.value}
                onCheckedChange={(checked) => handleFilterChange('customization', checked ? level.value : '')}
              />
              <Label htmlFor={`customization-${level.value}`} className="text-sm cursor-pointer">
                {level.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full rounded-none"
          data-testid="clear-filters-btn"
        >
          <X className="h-4 w-4 mr-2" />
          {t('products.clearFilters')}
        </Button>
      )}
    </div>
  );

  // Build breadcrumb
  const renderBreadcrumb = () => {
    const items = [
      { label: t('nav.home'), to: '/' },
      { label: t('nav.catalog'), to: '/catalogue' },
    ];
    
    if (parentCategory) {
      items.push({
        label: language === 'fr' ? parentCategory.name_fr : parentCategory.name_en,
        to: `/catalogue/${parentCategory.slug}`
      });
    }
    
    if (currentCategory && !parentCategory) {
      items.push({
        label: language === 'fr' ? currentCategory.name_fr : currentCategory.name_en,
        to: `/catalogue/${currentCategory.slug}`,
        active: true
      });
    } else if (currentCategory && parentCategory) {
      items.push({
        label: language === 'fr' ? currentCategory.name_fr : currentCategory.name_en,
        active: true
      });
    }
    
    return (
      <div className="flex items-center gap-2 text-sm flex-wrap">
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            {item.active || i === items.length - 1 && currentCategory ? (
              <span className="text-[#1F2937] font-medium">{item.label}</span>
            ) : (
              <Link to={item.to} className="text-muted-foreground hover:text-[#1F2937] transition-colors">
                {item.label}
              </Link>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="catalog-page">
      {/* Breadcrumb */}
      <div className="bg-secondary border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          {renderBreadcrumb()}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">
            {currentCategory
              ? (language === 'fr' ? currentCategory.name_fr : currentCategory.name_en)
              : t('nav.catalog')}
          </h1>
          {currentCategory && (
            <p className="text-muted-foreground max-w-2xl">
              {language === 'fr' ? currentCategory.description_fr : currentCategory.description_en}
            </p>
          )}
        </div>

        {/* Subcategory chips (when viewing a parent category) */}
        {currentCategory && !currentCategory.parent_id && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {getSubcategories(currentCategory.category_id).map((subcat) => (
                <Link
                  key={subcat.category_id}
                  to={`/catalogue/${currentCategory.slug}/${subcat.slug}`}
                  className="inline-flex items-center px-4 py-2 border border-border hover:border-[#DC2626] hover:text-[#DC2626] transition-colors text-sm"
                >
                  {language === 'fr' ? subcat.name_fr : subcat.name_en}
                  <ArrowRight className="ml-2 h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('products.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="rounded-none" data-testid="search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {/* Mobile Filter Toggle */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden rounded-none" data-testid="mobile-filter-toggle">
                <Filter className="h-4 w-4 mr-2" />
                {t('products.filters')}
                {hasActiveFilters && (
                  <span className="ml-2 w-5 h-5 bg-[#1F2937] text-white text-xs rounded-full flex items-center justify-center">
                    !
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <SheetHeader>
                <SheetTitle>{t('products.filters')}</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-12">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <FilterContent />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-square" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-secondary rounded-full flex items-center justify-center">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-4">{t('products.noResults')}</p>
                <Button variant="outline" onClick={clearFilters} className="rounded-none">
                  {t('products.clearFilters')}
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-6">
                  {total} {language === 'fr' ? 'produit(s) trouvé(s)' : 'product(s) found'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product, index) => (
                    <Link
                      key={product.product_id}
                      to={`/produit/${product.slug}`}
                      className="group product-card border border-border bg-white animate-slide-up hover:shadow-lg transition-shadow"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`product-${product.slug}`}
                    >
                      <div className="aspect-square bg-secondary overflow-hidden relative">
                        {product.featured && (
                          <span className="absolute top-2 left-2 z-10 bg-[#DC2626] text-white text-xs px-2 py-1 font-medium">
                            {language === 'fr' ? 'Populaire' : 'Popular'}
                          </span>
                        )}
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={language === 'fr' ? product.name_fr : product.name_en}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-secondary to-secondary/50">
                            <span className="text-4xl font-bold opacity-20">NFC</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold mb-1 line-clamp-2 min-h-[2.5rem]">
                          {language === 'fr' ? product.name_fr : product.name_en}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem]">
                          {language === 'fr' ? product.description_fr : product.description_en}
                        </p>
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          {product.materials?.slice(0, 2).map((mat) => (
                            <span key={mat} className="text-xs px-2 py-1 bg-secondary font-mono">
                              {mat}
                            </span>
                          ))}
                          {product.nfc_options?.[0]?.chip_type && (
                            <span className="text-xs px-2 py-1 bg-[#1F2937] text-white font-mono">
                              {product.nfc_options[0].chip_type}
                            </span>
                          )}
                        </div>
                        <span className="product-cta inline-flex items-center text-sm font-medium text-[#DC2626] group-hover:translate-x-1 transition-transform">
                          {t('products.requestQuote')}
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CatalogPage;
